import { motion } from 'motion/react'
import { Award, Target, Zap } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

type ROIFeature = {
  icon: React.ElementType
  title: string
  description: string
}

/*
  "Save 20h Per Review" and "90% Less Manual Work" were not measured; the one
  figure that was is 48%, from internal testing on three client projects, and
  it is stated with that population.

  "Zero Compliance Risks. Every review meets MDR standards automatically" is
  removed rather than reworded. No tool can guarantee a regulatory outcome, the
  people reading this page are the ones who assess that outcome, and a promise
  they know to be impossible costs more credibility than the card ever bought.
  What replaces it is a claim about structure, which is checkable against the
  workflow.
*/
const roiFeatures: ROIFeature[] = [
  {
    icon: Zap,
    title: '48% less time per review',
    description:
      'Measured in internal testing across three client projects. The saving comes from the screening and deduplication steps, which is where the repetitive work sits.',
  },
  {
    icon: Target,
    title: 'The search you can run again',
    description:
      'Search strings are stored verbatim, so the update is the same query against a new date range instead of a fresh search that happens to look similar.',
  },
  {
    icon: Award,
    title: 'Structured the way the guidance is',
    description:
      'The workflow follows the MEDDEV 2.7/1 Rev. 4 literature review structure, from PICO tables through screening and appraisal to the PRISMA flow.',
  },
]

export const ROISection = () => (
  <section className="relative bg-white py-24">
    {/* Subtle texture background */}
    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-[0.02] mix-blend-soft-light"></div>

    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-16 text-center"
      >
        <h2 className="mb-4 text-4xl font-bold text-[#001871] md:text-5xl">
          The Impact on Your Bottom Line
        </h2>
        <p className="text-xl text-gray-600">
          Calculate how much time and money you could save with SOTA Navigator.
        </p>
      </motion.div>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {roiFeatures.map((feature, index) => (
          <motion.div
            key={feature.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="border border-gray-100 bg-white transition-all duration-300 hover:shadow-xl">
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 rounded-xl bg-[#001871]/5 p-4">
                    <feature.icon className="size-10 text-[#001871]" />
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-[#001871]">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
)
